import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { decodeJwtPayload } from "@/lib/jwt-utils";

/**
 * 사용자 정보 타입
 */
interface User {
  userId: string;
  username: string;
  email?: string;
  name?: string;
  groups?: string[];
  [key: string]: unknown;
}

/**
 * 인증 상태 타입
 */
interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
}

/**
 * 인증 액션 타입
 */
interface AuthActions {
  setAuth: (
    user: User,
    accessToken: string,
    idToken: string,
    refreshToken?: string,
  ) => void;
  updateUser: (user: Partial<User>) => void;
  clearAuth: () => void;
  setLoading: (isLoading: boolean) => void;
  isAdmin: () => boolean;
  refreshTokens: () => Promise<boolean>;
}

/**
 * 인증 스토어 타입
 */
type AuthStore = AuthState & AuthActions;

/**
 * 초기 상태
 */
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  accessToken: null,
  idToken: null,
  refreshToken: null,
  isLoading: true,
};

/**
 * 인증 상태 관리 Zustand 스토어
 * - 로그인 시 사용자 정보와 토큰 저장
 * - localStorage에 persist하여 새로고침 시에도 유지
 * - 로그아웃 시 모든 인증 정보 제거
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * 로그인 시 인증 정보 설정
       */
      setAuth: (user, accessToken, idToken, refreshToken) => {
        set({
          isAuthenticated: true,
          user,
          accessToken,
          idToken,
          refreshToken: refreshToken || null,
        });
      },

      /**
       * 사용자 정보 업데이트
       */
      updateUser: (userData) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },

      /**
       * 로그아웃 시 인증 정보 초기화
       */
      clearAuth: () => {
        // localStorage에서 토큰 제거
        if (typeof window !== "undefined") {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("idToken");
          localStorage.removeItem("refreshToken");
        }
        // 스토어 초기화
        set({ ...initialState, isLoading: false });
      },

      /**
       * 로딩 상태 설정
       */
      setLoading: (isLoading) => {
        set({ isLoading });
      },

      /**
       * 관리자 권한 확인 (대소문자 무관)
       */
      isAdmin: () => {
        const state = get();
        return (
          state.user?.groups?.some(
            (group) => group.toLowerCase() === "admin",
          ) ?? false
        );
      },

      /**
       * refreshToken을 사용하여 accessToken과 idToken을 갱신
       * @returns 갱신 성공 여부
       */
      refreshTokens: async () => {
        const state = get();
        const refreshToken =
          state.refreshToken || localStorage.getItem("refreshToken");
        const username = state.user?.username;

        if (!refreshToken || !username) {
          console.error("[Auth Store] refreshToken 또는 username이 없습니다");
          return false;
        }

        try {
          const response = await fetch("/api/auth/refresh", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refreshToken,
              username,
            }),
          });

          const data = await response.json();

          if (!data.success || !data.data) {
            console.error("[Auth Store] 토큰 갱신 실패:", data.message);
            return false;
          }

          // 새로운 토큰으로 상태 업데이트
          const { accessToken, idToken } = data.data;

          // localStorage 업데이트
          localStorage.setItem("accessToken", accessToken);
          localStorage.setItem("idToken", idToken);

          // idToken에서 사용자 정보 추출
          const idTokenPayload = decodeJwtPayload(idToken);

          // 그룹 정보 추출
          let groups: string[] = [];
          if (idTokenPayload["custom:groups"]) {
            try {
              groups = JSON.parse(idTokenPayload["custom:groups"] as string);
            } catch {
              groups = [];
            }
          }
          if (!groups.length && idTokenPayload["cognito:groups"]) {
            groups = idTokenPayload["cognito:groups"] as string[];
          }

          // 사용자 정보 업데이트
          set({
            accessToken,
            idToken,
            user: {
              ...state.user!,
              groups,
            },
          });

          console.log("[Auth Store] 토큰 갱신 성공");
          return true;
        } catch (error) {
          console.error("[Auth Store] 토큰 갱신 중 오류:", error);
          return false;
        }
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      // isLoading은 persist하지 않음 (항상 초기값 true에서 시작)
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        accessToken: state.accessToken,
        idToken: state.idToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);
